import React, { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  Assessment as MetricsIcon,
  AccessTime as FRTIcon,
  Timer as ARTIcon,
  VerifiedUser as SLAIcon,
  ConfirmationNumber as TotalIcon,
  Refresh as RefreshIcon,
  DateRange as DateIcon,
  TrendingUp as TrendUpIcon,
  TrendingDown as TrendDownIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import './TicketStyles.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Metrics {
  avgFRT: number;
  avgART: number;
  slaAdherence: number;
  total: number;
}

function formatDuration(ms?: number): string {
  if (!ms || ms < 0) return '-';
  const sec = Math.round(ms / 1000);
  const day = Math.floor(sec / 86400);
  const hour = Math.floor((sec % 86400) / 3600);
  const minute = Math.floor((sec % 3600) / 60);
  const second = sec % 60;
  if (day >= 1) return `${day} روز ${hour} ساعت`;
  if (hour >= 1) return `${hour} ساعت ${minute} دقیقه`;
  if (minute >= 1) return `${minute} دقیقه`;
  return `${second} ثانیه`;
}

const AdvancedTicketMetricsPage: React.FC = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      const res = await axios.get(`${API_BASE_URL}/admin/tickets/metrics/advanced?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(res.data.data);
    } catch (error) {
      console.error('Error loading metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('fa-IR').format(num);
  };

  const getSLAColor = (value: number) => {
    if (value >= 90) return 'success';
    if (value >= 70) return 'warning';
    return 'danger';
  };

  return (
    <>
      <Helmet>
        <title>متریک‌های پیشرفته تیکت</title>
      </Helmet>

      <div className="ticket-page">
        {/* Header */}
        <div className="ticket-header">
          <div className="ticket-header-title">
            <h1>
              <MetricsIcon />
              متریک‌های پیشرفته
            </h1>
            <span className="ticket-header-subtitle">
              آمار عملکرد و تحلیل تیکت‌های پشتیبانی
            </span>
          </div>

          <div className="header-actions">
            <button className="header-btn" onClick={load} disabled={loading}>
              <RefreshIcon style={{ fontSize: 18 }} />
              بروزرسانی
            </button>
          </div>
        </div>

        {/* Date Filters */}
        <div className="filters-section">
          <div className="filters-header">
            <span className="filters-title">
              <DateIcon />
              فیلتر بازه زمانی
            </span>
          </div>
          <div className="filters-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="filter-group">
              <label className="filter-label">از تاریخ</label>
              <input
                type="date"
                className="filter-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">تا تاریخ</label>
              <input
                type="date"
                className="filter-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="filter-group" style={{ alignSelf: 'flex-end' }}>
              <button className="filter-btn primary" onClick={load} disabled={loading}>
                اعمال فیلتر
              </button>
            </div>
            <div className="filter-group" style={{ alignSelf: 'flex-end' }}>
              <button
                className="filter-btn secondary"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                پاک کردن
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-overlay">
            <CircularProgress size={40} />
            <p>در حال بارگذاری...</p>
          </div>
        ) : (
          <>
            {/* Metrics Cards */}
            <div className="metrics-grid">
              {/* FRT Card */}
              <div className="metric-card">
                <div className="metric-card-icon info">
                  <FRTIcon style={{ fontSize: 28 }} />
                </div>
                <div className="metric-card-value">
                  {metrics ? formatDuration(metrics.avgFRT) : '-'}
                </div>
                <div className="metric-card-label">
                  میانگین زمان اولین پاسخ (FRT)
                </div>
                <div className="metric-card-trend up">
                  <TrendUpIcon style={{ fontSize: 14 }} />
                  زمان پاسخگویی
                </div>
              </div>

              {/* ART Card */}
              <div className="metric-card">
                <div className="metric-card-icon primary">
                  <ARTIcon style={{ fontSize: 28 }} />
                </div>
                <div className="metric-card-value">
                  {metrics ? formatDuration(metrics.avgART) : '-'}
                </div>
                <div className="metric-card-label">
                  میانگین زمان حل تیکت (ART)
                </div>
                <div className="metric-card-trend up">
                  <TrendUpIcon style={{ fontSize: 14 }} />
                  زمان رفع مشکل
                </div>
              </div>

              {/* SLA Card */}
              <div className="metric-card">
                <div className={`metric-card-icon ${metrics ? getSLAColor(metrics.slaAdherence) : 'warning'}`}>
                  <SLAIcon style={{ fontSize: 28 }} />
                </div>
                <div className="metric-card-value">
                  {metrics ? `${formatNumber(metrics.slaAdherence)}%` : '-'}
                </div>
                <div className="metric-card-label">
                  رعایت SLA
                </div>
                {metrics && (
                  <div className={`metric-card-trend ${metrics.slaAdherence >= 80 ? 'up' : 'down'}`}>
                    {metrics.slaAdherence >= 80 ? (
                      <TrendUpIcon style={{ fontSize: 14 }} />
                    ) : (
                      <TrendDownIcon style={{ fontSize: 14 }} />
                    )}
                    {metrics.slaAdherence >= 90 ? 'عالی' : metrics.slaAdherence >= 70 ? 'قابل قبول' : 'نیاز به بهبود'}
                  </div>
                )}
              </div>

              {/* Total Card */}
              <div className="metric-card">
                <div className="metric-card-icon success">
                  <TotalIcon style={{ fontSize: 28 }} />
                </div>
                <div className="metric-card-value">
                  {metrics ? formatNumber(metrics.total) : '-'}
                </div>
                <div className="metric-card-label">
                  تعداد کل تیکت‌ها
                </div>
                <div className="metric-card-trend up">
                  <TrendUpIcon style={{ fontSize: 14 }} />
                  در بازه انتخابی
                </div>
              </div>
            </div>

            {/* Info Cards */}
            <div className="cards-grid" style={{ marginTop: 24 }}>
              <div className="item-card">
                <div className="item-card-header">
                  <h3 className="item-card-title">
                    <FRTIcon style={{ fontSize: 18, marginLeft: 8, color: 'var(--color-primary)' }} />
                    زمان اولین پاسخ (FRT)
                  </h3>
                </div>
                <p className="item-card-content">
                  میانگین زمانی که طول می‌کشد تا یک تیکت اولین پاسخ را از طرف تیم پشتیبانی دریافت کند.
                  این معیار مهمی برای سنجش سرعت پاسخگویی تیم است.
                </p>
              </div>

              <div className="item-card">
                <div className="item-card-header">
                  <h3 className="item-card-title">
                    <ARTIcon style={{ fontSize: 18, marginLeft: 8, color: 'var(--color-primary)' }} />
                    زمان حل مشکل (ART)
                  </h3>
                </div>
                <p className="item-card-content">
                  میانگین زمانی که از ایجاد تیکت تا حل شدن آن طول می‌کشد.
                  این معیار نشان‌دهنده کارایی کلی تیم در حل مشکلات است.
                </p>
              </div>

              <div className="item-card">
                <div className="item-card-header">
                  <h3 className="item-card-title">
                    <SLAIcon style={{ fontSize: 18, marginLeft: 8, color: 'var(--color-success)' }} />
                    رعایت SLA
                  </h3>
                </div>
                <p className="item-card-content">
                  درصد تیکت‌هایی که در زمان تعیین‌شده (طبق توافق سطح خدمات) پاسخ داده شده‌اند.
                  هدف حفظ این نرخ بالای ۹۰٪ است.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AdvancedTicketMetricsPage;
